import { useEffect, useState } from 'react';
import { ExternalLink, Hash, Images, UserRound } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  TICKET_ORDER_CHANNEL,
  TICKET_ORDER_CHANNEL_LABELS,
  TICKET_RESOLUTION,
  TICKET_RESOLUTION_LABELS,
  SUPPORT_ERRORS,
  parseSubscriptionIssueMessage,
  formatTicketListTitle,
  suggestTicketCreditAmount,
} from '../../api/support';
import form from '../../styles/form.module.css';
import styles from './TicketDrawer.module.css';

const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'CLOSED'];
const DECISION_NONE = '';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function TicketDrawer({ ticket, onClose, onRespond }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('OPEN');
  const [opsNote, setOpsNote] = useState('');
  const [decision, setDecision] = useState(DECISION_NONE);
  const [creditAmount, setCreditAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const parsedIssue = ticket ? parseSubscriptionIssueMessage(ticket.message) : null;
  const isSubs = ticket?.orderChannel === TICKET_ORDER_CHANNEL.SUBS;
  const alreadyResolved = Boolean(ticket?.resolution);
  const canRetryRequireReturn = ticket?.resolution === TICKET_RESOLUTION.REQUIRE_RETURN
    && !(ticket?.returnIds?.length);
  const canDecide = Boolean(ticket?.orderId) && (!alreadyResolved || canRetryRequireReturn);

  useEffect(() => {
    if (!ticket) return;
    setStatus(ticket.status || 'OPEN');
    setOpsNote(ticket.opsNote || '');
    setDecision(canRetryRequireReturn ? TICKET_RESOLUTION.REQUIRE_RETURN : DECISION_NONE);
    setCreditAmount(
      ticket.creditAmount != null && ticket.creditAmount > 0
        ? String(ticket.creditAmount)
        : '',
    );
    setError('');

    let cancelled = false;
    if (canDecide && !ticket.creditAmount) {
      suggestTicketCreditAmount(ticket, user?.idToken).then((amount) => {
        if (cancelled || amount == null) return;
        setCreditAmount((prev) => (prev ? prev : String(amount)));
      });
    }
    return () => { cancelled = true; };
  }, [ticket, user?.idToken, canDecide, canRetryRequireReturn]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const resolution = canDecide && decision ? decision : undefined;
      if (resolution === TICKET_RESOLUTION.DISPOSE) {
        const amount = Number(creditAmount);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw Object.assign(new Error('credit_amount_must_be_positive'), {
            code: 'credit_amount_must_be_positive',
          });
        }
      }
      await onRespond(ticket, {
        status,
        opsNote: opsNote.trim(),
        resolution,
        creditAmount: resolution === TICKET_RESOLUTION.DISPOSE ? creditAmount : undefined,
      });
    } catch (err) {
      const code = err?.code || err?.message;
      setError(
        SUPPORT_ERRORS[code]
        || err?.detail
        || err?.message
        || 'Could not update this ticket. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const primaryLabel = (() => {
    if (submitting) return 'Saving…';
    if (decision === TICKET_RESOLUTION.DISPOSE) {
      return creditAmount ? `Dispose & credit ₹${creditAmount}` : 'Dispose & credit';
    }
    if (decision === TICKET_RESOLUTION.REQUIRE_RETURN) return 'Require return';
    return 'Update ticket';
  })();

  return (
    <Drawer
      open={!!ticket}
      onClose={handleClose}
      title={ticket ? formatTicketListTitle(ticket) : 'Support ticket'}
      subtitle={ticket ? `Ticket #${ticket.ticketId}` : undefined}
      width={460}
      footer={(
        <>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {primaryLabel}
          </Button>
        </>
      )}
    >
      {ticket && (
        <form onSubmit={handleSubmit}>
          <AuthMessage>{error}</AuthMessage>

          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <UserRound size={13} />
              <span title={ticket.customerSub}>{ticket.customerSub}</span>
            </div>
            {ticket.orderId && (
              <div className={styles.metaItem}>
                <Hash size={13} />
                <span>{ticket.orderId}</span>
                {isSubs && (
                  <span className={styles.channelBadgeSubs}>
                    {TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS]}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className={styles.timestamps}>
            <span>Raised {formatDate(ticket.createdAt)}</span>
            <span>Updated {formatDate(ticket.updatedAt)}</span>
          </div>

          {alreadyResolved && (
            <div className={styles.resolvedBanner}>
              <strong>{ticket.resolutionLabel || TICKET_RESOLUTION_LABELS[ticket.resolution]}</strong>
              {ticket.resolution === TICKET_RESOLUTION.DISPOSE && ticket.creditAmount != null && (
                <> · ₹{ticket.creditAmount} credited</>
              )}
              {ticket.returnIds?.length > 0 && (
                <> · RMA {ticket.returnIds.join(', ')}</>
              )}
              {ticket.resolvedAt && (
                <span className={styles.resolvedWhen}> · {formatDate(ticket.resolvedAt)}</span>
              )}
              {canRetryRequireReturn && (
                <p className={styles.retryHint}>
                  No return was created last time — you can retry Require return below.
                </p>
              )}
            </div>
          )}

          <div className={styles.messageBox}>
            <p className={styles.messageLabel}>
              {parsedIssue ? 'Issue details' : 'Customer message'}
            </p>
            {parsedIssue ? (
              <dl className={styles.issueFields}>
                <div className={styles.issueField}>
                  <dt>Product</dt>
                  <dd>{parsedIssue.productName}</dd>
                </div>
                <div className={styles.issueField}>
                  <dt>Reason</dt>
                  <dd>{parsedIssue.reasonLabel}</dd>
                </div>
                <div className={styles.issueField}>
                  <dt>Order</dt>
                  <dd>{parsedIssue.orderId || ticket.orderId || '—'}</dd>
                </div>
                {parsedIssue.lineValue && (
                  <div className={styles.issueField}>
                    <dt>Line value</dt>
                    <dd>{parsedIssue.lineValue}</dd>
                  </div>
                )}
                {parsedIssue.customerNote && (
                  <div className={styles.issueField}>
                    <dt>Customer note</dt>
                    <dd className={styles.issueNote}>{parsedIssue.customerNote}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className={styles.messageText}>{ticket.message}</p>
            )}
          </div>

          {Array.isArray(ticket.imageUrls) && ticket.imageUrls.length > 0 && (
            <div className={styles.photosBlock}>
              <p className={styles.messageLabel}>
                <Images size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Customer photos ({ticket.imageUrls.length})
              </p>
              <div className={styles.photoGrid}>
                {ticket.imageUrls.map((url, index) => (
                  <a
                    key={`evidence-${index}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.photoThumbWrap}
                  >
                    <img src={url} alt="Customer evidence" className={styles.photoThumb} />
                    <span className={styles.photoThumbLabel}>
                      <ExternalLink size={10} /> Open
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {canDecide && (
            <div className={styles.decisionBlock}>
              <p className={styles.decisionTitle}>Store decision</p>
              <p className={styles.decisionHint}>
                For failed delivery or product issues: credit the wallet now (customer keeps/disposes),
                or create a return for rider pickup via Return &amp; Exchange.
              </p>
              <div className={styles.decisionOptions}>
                <label className={`${styles.decisionOption} ${decision === DECISION_NONE ? styles.decisionOptionActive : ''}`}>
                  <input
                    type="radio"
                    name="ticket-decision"
                    value={DECISION_NONE}
                    checked={decision === DECISION_NONE}
                    onChange={() => setDecision(DECISION_NONE)}
                    disabled={submitting}
                  />
                  <span>
                    <strong>Status / note only</strong>
                    <em>No wallet credit or return yet</em>
                  </span>
                </label>
                <label className={`${styles.decisionOption} ${decision === TICKET_RESOLUTION.DISPOSE ? styles.decisionOptionActive : ''}`}>
                  <input
                    type="radio"
                    name="ticket-decision"
                    value={TICKET_RESOLUTION.DISPOSE}
                    checked={decision === TICKET_RESOLUTION.DISPOSE}
                    onChange={() => setDecision(TICKET_RESOLUTION.DISPOSE)}
                    disabled={submitting}
                  />
                  <span>
                    <strong>Dispose + credit</strong>
                    <em>Add store credit now and close the ticket</em>
                  </span>
                </label>
                <label className={`${styles.decisionOption} ${decision === TICKET_RESOLUTION.REQUIRE_RETURN ? styles.decisionOptionActive : ''}`}>
                  <input
                    type="radio"
                    name="ticket-decision"
                    value={TICKET_RESOLUTION.REQUIRE_RETURN}
                    checked={decision === TICKET_RESOLUTION.REQUIRE_RETURN}
                    onChange={() => setDecision(TICKET_RESOLUTION.REQUIRE_RETURN)}
                    disabled={submitting}
                  />
                  <span>
                    <strong>Require return</strong>
                    <em>Create pickup RMA — assign rider in Return &amp; Exchange</em>
                  </span>
                </label>
              </div>

              {decision === TICKET_RESOLUTION.DISPOSE && (
                <label className={form.field}>
                  <span className={form.label}>Wallet credit (₹)</span>
                  <input
                    className={form.input}
                    type="number"
                    min="1"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="e.g. 35"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    disabled={submitting}
                    required
                  />
                  <span className={form.hint}>
                    Customer is notified and the ticket closes when you save.
                  </span>
                </label>
              )}

              {decision === TICKET_RESOLUTION.REQUIRE_RETURN && (
                <p className={styles.handoffHint}>
                  Saving creates return request(s) for this order. Finish pickup and credit in{' '}
                  <strong>Return &amp; Exchange</strong>.
                </p>
              )}
            </div>
          )}

          {!ticket.orderId && (
            <p className={styles.handoffHint}>
              No order is linked on this ticket, so dispose/credit and require-return are unavailable.
              Update status and note only.
            </p>
          )}

          {decision !== TICKET_RESOLUTION.DISPOSE && (
            <label className={form.field}>
              <span className={form.label}>Status</span>
              <select
                className={form.select}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={submitting}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </label>
          )}

          <label className={form.field}>
            <span className={form.label}>Note to customer</span>
            <textarea
              className={form.textarea}
              placeholder={
                decision === TICKET_RESOLUTION.DISPOSE
                  ? 'e.g. ₹35 store credit added — you may dispose of the item safely.'
                  : decision === TICKET_RESOLUTION.REQUIRE_RETURN
                    ? 'e.g. Please keep the product ready — we will arrange a pickup.'
                    : 'e.g. We are looking into this and will update you shortly.'
              }
              value={opsNote}
              onChange={(e) => setOpsNote(e.target.value)}
              rows={3}
              disabled={submitting}
            />
            <span className={form.hint}>Sent to the customer as a push notification when you save.</span>
          </label>

          <div className={styles.currentStatusRow}>
            <span className={form.hint}>Current status:</span>
            <StatusBadge status={ticket.status} size="sm" />
          </div>
        </form>
      )}
    </Drawer>
  );
}
